import type { TaskResult } from "../types";
import {
  type FuncionProjectSpec,
  type TaskCheck,
  findTable,
  tableColumnRange,
} from "../data/funciones/spec";
import {
  type CheckResult,
  checkBlock,
  checkCell,
  checkSpill,
  countFunction,
  spanishName,
} from "./functionChecks";

function rangeOf(spec: FuncionProjectSpec, check: TaskCheck): string {
  return check.kind === "column"
    ? tableColumnRange(findTable(spec, check.table), check.column)
    : check.address;
}

/** The first cell's formula must call each function at least N times. */
async function checkMinCounts(
  context: Excel.RequestContext,
  check: TaskCheck,
  address: string
): Promise<CheckResult> {
  if (!check.minCounts) return { passed: true };

  const firstCell = context.workbook.worksheets
    .getItem(check.sheet)
    .getRange(address)
    .getCell(0, 0);
  firstCell.load("formulas");
  await context.sync();

  for (const [fn, times] of Object.entries(check.minCounts)) {
    if (countFunction(firstCell.formulas[0][0], fn) < times) {
      return {
        passed: false,
        message: `Esta tarea necesita ${times} ${spanishName(fn)} en la misma fórmula (uno por cada caso).`,
      };
    }
  }
  return { passed: true };
}

/** Grades every task of a Funciones project from its spec. Never throws. */
export async function gradeFuncionProject(
  spec: FuncionProjectSpec
): Promise<TaskResult[]> {
  const results: TaskResult[] = [];

  for (const [id, check] of Object.entries(spec.checks)) {
    const taskId = Number(id);
    try {
      const result = await Excel.run(async (context) => {
        const address = rangeOf(spec, check);
        const valueCheck =
          check.kind === "cell"
            ? await checkCell(context, check.sheet, address, check.fns, check.expected, check.exact)
            : check.kind === "spill"
            ? await checkSpill(context, check.sheet, address, check.fns, check.expected, check.exact)
            : await checkBlock(
                context,
                check.sheet,
                address,
                check.fns,
                check.kind === "column"
                  ? check.expected.map((value) => [value])
                  : check.expected,
                check.exact
              );
        if (!valueCheck.passed) return valueCheck;
        return checkMinCounts(context, check, address);
      });
      results.push({ taskId, ...result });
    } catch {
      results.push({ taskId, passed: false });
    }
  }

  return results;
}
